import {
  Scissors,
  Stethoscope,
  House,
  GraduationCap,
  PawPrint,
  Sun,
  Truck,
  ShoppingBag,
  Check,
  ShieldWarning,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { OnboardingLabels, SubVerticalCategory, SubVerticalOption } from "./types";

export interface SubVerticalGridItemProps {
  option: SubVerticalOption;
  selected: boolean;
  locale: "tr" | "en";
  onSelect: (key: string) => void;
  labels: OnboardingLabels["business"];
  className?: string;
}

const ICONS: Record<SubVerticalCategory, PhosphorIcon> = {
  grooming: Scissors,
  veterinary: Stethoscope,
  boarding: House,
  training: GraduationCap,
  walking: PawPrint,
  daycare: Sun,
  "mobile-service": Truck,
  retail: ShoppingBag,
};

export function SubVerticalGridItem({ option, selected, locale, onSelect, labels, className }: SubVerticalGridItemProps) {
  const Icon = ICONS[option.category];
  const hasCompliance = !!option.complianceHints?.length;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(option.key)}
      className={cn(
        "group relative flex h-full min-h-[7rem] flex-col items-start gap-2 rounded-biz-card border-2 p-4 text-left transition-all duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
        selected
          ? "border-biz-primary bg-biz-primary/5 shadow-biz-md"
          : "border-biz-border bg-biz-surface hover:border-biz-primary/50 hover:bg-biz-surface-elevated",
        className,
      )}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <span
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
            selected
              ? "bg-biz-primary text-biz-primary-foreground"
              : "bg-biz-muted text-biz-foreground group-hover:bg-biz-primary/10 group-hover:text-biz-primary",
          )}
        >
          <Icon size={20} aria-hidden />
        </span>
        {selected && (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-biz-primary text-biz-primary-foreground">
            <Check size={14} aria-hidden />
          </span>
        )}
      </div>

      <div className="flex-1">
        <p className="font-display text-sm font-semibold leading-tight text-biz-foreground">
          {option.displayName[locale]}
        </p>
        <p className="mt-1 line-clamp-2 text-xs leading-snug text-biz-foreground-muted">
          {option.description[locale]}
        </p>
      </div>

      {hasCompliance && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
          <ShieldWarning size={12} aria-hidden />
          {labels.complianceBadge}
        </span>
      )}
    </button>
  );
}
